import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import { store } from 'redux-rest-reducer';
import styled, { css } from 'styled-components';
import memoize from 'memoize-one';
import { getAll as getPricingMatrix } from 'actions/pricing-matrix';
import { ADMIN_BASE } from 'constants/api';

const DURATIONS = [1, 3, 6, 12, 18, 24];

const C = {
  panel: '#fff',
  ink: '#1a1f36',
  muted: '#6b7280',
  line: '#e5e7eb',
  accent: '#4f2fbf',
  accentSoft: '#efeaff',
  head: '#faf9fe',
  group: '#f0edfb',
  group2: '#eef7f1',
  na: '#c2c7d0',
  conflict: '#c2410c',
  rowHover: '#faf9ff',
  shadow: '0 1px 2px rgba(16,24,40,.06), 0 1px 3px rgba(16,24,40,.1)',
};

// [background, color] per tier / billing-unit badge — ported from the mockup.
const TIER_COLORS = {
  IBG: ['#e6f4ea', '#0a7d4d'],
  IBG_HIGH: ['#dcefe4', '#076b41'],
  HIGH: ['#e7edff', '#3b5bdb'],
  PLUS: ['#fdeede', '#b45309'],
  REGULAR: ['#eef0f3', '#4b5563'],
  BASIC: ['#f2effc', '#6b46c1'],
  CLASS_K5: ['#fbe9f2', '#a3216b'],
  HYBRID: ['#e0f2fe', '#0369a1'],
};
const DEFAULT_TIER_COLOR = ['#eef0f3', '#4b5563'];

const UNIT_COLORS = {
  WEEK: ['#e7edff', '#3b5bdb'],
  MONTH: ['#fdeede', '#b45309'],
  G: ['#eef0f3', '#4b5563'],
};

const SUBTITLE_COLORS = {
  sub: ['#e7edff', '#3b5bdb'],
  onetime: ['#e6f4ea', '#0a7d4d'],
};

const Wrap = styled.div`
  max-width: 1120px;
  margin: 0 auto;
  padding: 4px 20px 60px;
  color: ${C.ink};
  font-size: 14px;
  line-height: 1.45;
`;

const Title = styled.h1`
  font-size: 22px;
  margin: 10px 0 2px;
  letter-spacing: -0.2px;
`;

const Sub = styled.p`
  color: ${C.muted};
  font-size: 13px;
  margin: 0 0 16px;

  code {
    background: ${C.accentSoft};
    color: ${C.accent};
    padding: 1px 6px;
    border-radius: 5px;
    font-size: 12px;
  }
`;

const Live = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: ${C.panel};
  border: 1px solid ${C.line};
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 12px;
  color: ${C.muted};
  box-shadow: ${C.shadow};

  &:before {
    content: '';
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #0a7d4d;
    box-shadow: 0 0 0 3px rgba(10, 125, 77, 0.15);
  }
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin: 14px 0 6px;
`;

const Search = styled.div`
  flex: 1;
  min-width: 180px;
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${C.panel};
  border: 1px solid ${C.line};
  border-radius: 9px;
  padding: 8px 11px;
  box-shadow: ${C.shadow};
  color: ${C.muted};

  input {
    border: 0;
    background: transparent;
    color: ${C.ink};
    outline: none;
    width: 100%;
    font-size: 13px;
  }
`;

const Select = styled.select`
  background: ${C.panel};
  border: 1px solid ${C.line};
  border-radius: 9px;
  padding: 8px 12px;
  font-size: 13px;
  color: ${C.ink};
  box-shadow: ${C.shadow};
  cursor: pointer;
`;

const Formula = styled.p`
  color: ${C.muted};
  font-size: 12px;
  margin: 8px 2px 14px;

  code {
    background: ${C.accentSoft};
    color: ${C.accent};
    padding: 1px 6px;
    border-radius: 5px;
  }
  b.conflict {
    color: ${C.conflict};
    font-weight: 600;
  }
`;

const Card = styled.div`
  background: ${C.panel};
  border: 1px solid ${C.line};
  border-radius: 13px;
  box-shadow: ${C.shadow};
  overflow: hidden;
`;

const Scroll = styled.div`
  overflow: auto;
  max-height: calc(100vh - 230px);
`;

const Table = styled.table`
  border-collapse: collapse;
  width: 100%;
  font-size: 13px;
`;

const Th = styled.th`
  position: sticky;
  top: 0;
  z-index: 2;
  background: ${C.head};
  text-align: ${({ left }) => (left ? 'left' : 'right')};
  padding: 10px 12px;
  font-weight: 600;
  color: ${({ dur }) => (dur ? C.ink : C.muted)};
  border-bottom: 1px solid ${C.line};
  white-space: nowrap;
  font-size: 12px;

  ${({ tight }) =>
    tight &&
    css`
      width: 1%;
    `};
`;

const GroupRow = styled.tr`
  td {
    background: ${({ onetime }) => (onetime ? C.group2 : C.group)};
    font-weight: 700;
    color: ${C.ink};
    padding: 8px 12px;
    font-size: 12.5px;
    letter-spacing: 0.3px;
    border-bottom: 1px solid ${C.line};
  }
`;

const CurBadge = styled.span`
  color: ${C.muted};
  font-weight: 600;
  font-size: 11px;
  background: ${C.panel};
  padding: 1px 7px;
  border-radius: 5px;
  margin-left: 6px;
`;

const SubtitleBadge = styled.span`
  font-weight: 600;
  font-size: 11px;
  padding: 1px 8px;
  border-radius: 5px;
  margin-left: 8px;
  background: ${({ kind }) => (SUBTITLE_COLORS[kind] || SUBTITLE_COLORS.sub)[0]};
  color: ${({ kind }) => (SUBTITLE_COLORS[kind] || SUBTITLE_COLORS.sub)[1]};
`;

const Count = styled.span`
  color: ${C.muted};
  font-weight: 500;
  font-size: 11.5px;
`;

const DataRow = styled.tr`
  &:hover td {
    background: ${C.rowHover};
  }
`;

const Td = styled.td`
  padding: 9px 12px;
  border-bottom: 1px solid ${C.line};
  text-align: ${({ left }) => (left ? 'left' : 'right')};
  white-space: nowrap;
  color: ${({ na }) => (na ? C.na : 'inherit')};

  ${({ tight }) =>
    tight &&
    css`
      width: 1%;
    `};
`;

const Geo = styled.span`
  font-weight: 600;
`;

const Band = styled.span`
  color: ${C.muted};
  font-weight: 500;
  font-size: 12px;
`;

const Tag = styled.span`
  font-size: 10.5px;
  color: ${C.accent};
  background: ${C.accentSoft};
  padding: 1px 6px;
  border-radius: 5px;
  margin-left: 3px;
`;

const Cur = styled.span`
  color: ${C.muted};
`;

const TierBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  background: ${({ tier }) => (TIER_COLORS[tier] || DEFAULT_TIER_COLOR)[0]};
  color: ${({ tier }) => (TIER_COLORS[tier] || DEFAULT_TIER_COLOR)[1]};
`;

const PriceValue = styled.span`
  display: inline-block;
  padding: 2px 7px;
  border-radius: 6px;
  font-variant-numeric: tabular-nums;
  cursor: pointer;
  font-weight: ${({ conflict }) => (conflict ? 600 : 'normal')};
  color: ${({ conflict }) => (conflict ? C.conflict : 'inherit')};

  &:hover {
    background: ${C.accent};
    color: #fff;
  }
`;

const EmptyRow = styled.tr`
  td {
    text-align: center;
    color: ${C.muted};
    padding: 26px;
  }
`;

const Modal = styled.div`
  position: fixed;
  z-index: 50;
  width: 340px;
  max-height: 72vh;
  overflow: auto;
  background: ${C.panel};
  border: 1px solid ${C.line};
  border-radius: 12px;
  box-shadow: 0 8px 28px rgba(16, 24, 40, 0.18);
  padding: 12px 14px;
  font-size: 12.5px;

  h4 {
    margin: 0 0 2px;
    font-size: 13px;
  }
  .mh {
    color: ${C.muted};
    font-size: 11.5px;
    margin-bottom: 9px;
  }
  .envnote {
    color: ${C.muted};
    font-size: 11px;
    margin-top: 10px;
  }
`;

const PlanLine = styled.div`
  border-top: 1px solid ${C.line};
  padding: 8px 0;
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 2px 10px;

  &:first-of-type {
    border-top: 0;
  }

  .u {
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.3px;
    padding: 2px 6px;
    border-radius: 5px;
    align-self: start;
  }
  .t1 {
    color: ${C.ink};
    font-weight: 600;
  }
  .m {
    color: ${C.muted};
  }
  .hl {
    color: ${C.accent};
    font-weight: 700;
  }
  .id {
    color: ${C.muted};
    font-size: 11px;
    word-break: break-all;
    font-family: ui-monospace, Menlo, monospace;
  }
  a {
    color: ${C.accent};
    font-weight: 600;
    text-decoration: none;
  }
  a:hover {
    text-decoration: underline;
  }
`;

const UnitBadge = styled.span`
  background: ${({ unit }) => (UNIT_COLORS[unit] || UNIT_COLORS.G)[0]};
  color: ${({ unit }) => (UNIT_COLORS[unit] || UNIT_COLORS.G)[1]};
`;

const uniq = arr => arr.filter((v, i) => arr.indexOf(v) === i);

// The gateway wraps list responses as { value: ... } (redux-rest-reducer
// reads `value`). Normalise to the blocks array regardless of whether the
// payload is the array itself or a { blocks: [...] } envelope.
const extractBlocks = data => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.blocks)) return data.blocks;
  return [];
};

const matchRow = (block, row, q, tier) => {
  if (tier && row.tier !== tier) return false;
  if (q) {
    const hay = [
      block.geo,
      block.cur,
      row.tier,
      row.band_label,
      (row.tags || []).join(' '),
      block.subtitle || '',
    ]
      .join(' ')
      .toLowerCase();
    if (!hay.includes(q.toLowerCase().trim())) return false;
  }
  return true;
};

class PricingStructure extends PureComponent {
  static propTypes = {
    pricingMatrix: PropTypes.shape({}),
    actions: PropTypes.shape({
      getPricingMatrix: PropTypes.func.isRequired,
    }).isRequired,
  };

  static defaultProps = {
    pricingMatrix: null,
  };

  state = {
    q: '',
    region: '',
    currency: '',
    tier: '',
    hover: null,
    pos: { left: 0, top: 0 },
  };

  componentDidMount() {
    this.props.actions.getPricingMatrix();
  }

  componentDidUpdate(prevProps, prevState) {
    // Re-clamp the modal once it is mounted and we can measure it, so it never
    // overflows the viewport (mirrors the mockup's post-render positioning).
    if (this.state.hover && this.modalEl && this.anchorRect) {
      const w = this.modalEl.offsetWidth || 340;
      const h = this.modalEl.offsetHeight || 0;
      const pos = this.computePos(this.anchorRect, w, h);
      if (pos.left !== this.state.pos.left || pos.top !== this.state.pos.top) {
        this.setState({ pos });
      }
    }
  }

  componentWillUnmount() {
    clearTimeout(this.hideTimer);
  }

  computePos = (rect, w, h) => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let x = rect.right + 12;
    if (x + w > vw - 10) x = rect.left - w - 12;
    if (x < 10) x = 10;
    let y = rect.top;
    if (y + h > vh - 10) y = vh - h - 10;
    if (y < 10) y = 10;
    return { left: x, top: y };
  };

  onValueEnter = (e, hover) => {
    clearTimeout(this.hideTimer);
    this.anchorRect = e.currentTarget.getBoundingClientRect();
    this.setState({ hover, pos: this.computePos(this.anchorRect, 340, 0) });
  };

  onValueLeave = () => {
    this.hideTimer = setTimeout(() => this.setState({ hover: null }), 180);
  };

  onModalEnter = () => clearTimeout(this.hideTimer);

  onModalLeave = () => this.onValueLeave();

  onFilter = key => e => this.setState({ [key]: e.target.value });

  getBlocks() {
    const { data } = store.getAll(this.props.pricingMatrix) || {};
    return extractBlocks(data);
  }

  // Filter-option lists derived from the payload. Memoized on `blocks` so
  // hover/pos state changes do not recompute them.
  buildFilterOptions = memoize(blocks => ({
    geos: uniq(blocks.map(b => b.geo)),
    currencies: uniq(blocks.map(b => b.cur)),
    tiers: uniq(blocks.reduce((acc, b) => acc.concat(b.rows.map(r => r.tier)), [])),
  }));

  // The rendered matrix rows. Memoized on (blocks + the four filter inputs)
  // so hovering a price (which only touches hover/pos state) reuses the exact
  // same element instances and React skips re-reconciling the table.
  buildRows = memoize((blocks, q, region, currency, tier) => {
    const rowsOut = [];
    let shown = 0;

    blocks.forEach((block, bi) => {
      if (region && block.geo !== region) return;
      if (currency && block.cur !== currency) return;
      const rows = block.rows.filter(row => matchRow(block, row, q, tier));
      if (!rows.length) return;

      rowsOut.push(
        <GroupRow key={`g-${bi}`} onetime={block.kind === 'onetime'}>
          <td colSpan={9}>
            {block.geo}
            <CurBadge>{block.cur}</CurBadge>
            {block.subtitle ? (
              <SubtitleBadge kind={block.kind}>{block.subtitle}</SubtitleBadge>
            ) : null}
            <Count>
              {' '}
              &middot; {rows.length} group{rows.length > 1 ? 's' : ''}
            </Count>
          </td>
        </GroupRow>,
      );

      rows.forEach((row, ri) => {
        shown += 1;
        rowsOut.push(
          <DataRow key={`r-${bi}-${ri}`}>
            <Td left tight>
              <Geo>{block.geo}</Geo> <Band>[{row.band_label}]</Band>
              {(row.tags || []).map(t => (
                <Tag key={t}>{t}</Tag>
              ))}
            </Td>
            <Td left tight>
              <Cur>{block.cur}</Cur>
            </Td>
            <Td left tight>
              <TierBadge tier={row.tier}>{row.tier}</TierBadge>
            </Td>
            {DURATIONS.map(d => {
              const cell = row.cells[d];
              if (!cell) {
                return (
                  <Td key={d} na>
                    &mdash;
                  </Td>
                );
              }
              return (
                <Td key={d}>
                  <PriceValue
                    conflict={cell.conflict}
                    onMouseEnter={e =>
                      this.onValueEnter(e, {
                        plans: cell.plans,
                        geo: block.geo,
                        tier: row.tier,
                        cur: block.cur,
                        kind: block.kind,
                        dur: d,
                      })
                    }
                    onMouseLeave={this.onValueLeave}
                  >
                    {cell.val}
                  </PriceValue>
                </Td>
              );
            })}
          </DataRow>,
        );
      });
    });

    if (!shown) {
      rowsOut.push(
        <EmptyRow key="empty">
          <td colSpan={9}>No plans match these filters.</td>
        </EmptyRow>,
      );
    }

    return { rowsOut, shown };
  });

  renderModal() {
    const { hover, pos } = this.state;
    if (!hover) return null;
    const { plans, geo, tier, cur, kind, dur } = hover;
    const isOneTime = kind === 'onetime';

    return (
      <Modal
        ref={el => {
          this.modalEl = el;
        }}
        style={{ left: pos.left, top: pos.top }}
        onMouseEnter={this.onModalEnter}
        onMouseLeave={this.onModalLeave}
      >
        <h4>
          {geo} &middot; {tier} &middot; {dur}-month
          {isOneTime ? ' · one-time' : ''}
        </h4>
        <div className="mh">
          {plans.length} plan{plans.length > 1 ? 's' : ''} &middot; per-class = amount
          &divide; classes
        </div>
        {plans.map(p =>
          isOneTime ? (
            <PlanLine key={p.id}>
              <UnitBadge className="u" unit="G">
                G{p.grade}
              </UnitBadge>
              <div>
                <div className="t1">
                  Per-class {cur} {p.pc} &middot; {p.cpw} classes/wk
                </div>
                <div className="m">
                  1:{p.ratio} &middot; {p.durm}-mo &middot; total{' '}
                  <b className="hl">
                    {p.amount} {cur}
                  </b>{' '}
                  &middot; <b className="hl">{p.cat}</b>
                </div>
                <div className="id">{p.id}</div>
                <a
                  href={`${ADMIN_BASE}/pricing/admin/onlineplan/details/?id=${p.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open online-plan &#8599;
                </a>
              </div>
            </PlanLine>
          ) : (
            <PlanLine key={p.id}>
              <UnitBadge className="u" unit={p.unit}>
                {p.unit}
              </UnitBadge>
              <div>
                <div className="t1">
                  Per-class {cur} {p.pc} &middot; {p.cpw} classes/wk
                </div>
                <div className="m">
                  {p.cyc} cycle &middot; 1:{p.ratio} &middot; total{' '}
                  <b className="hl">
                    {p.amount} {cur}
                  </b>{' '}
                  &middot; <b className="hl">{p.gw}</b>
                </div>
                <div className="id">{p.id}</div>
                <a
                  href={`${ADMIN_BASE}/payment/admin/subscriptionplan/details/?id=${p.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open in payment admin &#8599;
                </a>
              </div>
            </PlanLine>
          ),
        )}
        <div className="envnote">
          Links resolve to the environment&apos;s admin host; subscription &rarr; payment
          admin, admissions &rarr; pricing online-plan admin.
        </div>
      </Modal>
    );
  }

  render() {
    const { q, region, currency, tier } = this.state;
    const blocks = this.getBlocks();

    const { geos, currencies, tiers } = this.buildFilterOptions(blocks);
    const { rowsOut, shown } = this.buildRows(blocks, q, region, currency, tier);

    return (
      <Wrap>
        <Title>Plan Pricing Structure</Title>
        <Sub>
          Live per-class pricing &middot; subscription from{' '}
          <code>payment.subscription_plan</code>, India admissions from{' '}
          <code>pricing.online_plan</code> (band IN) &middot;{' '}
          <Live>{shown} groups shown</Live>
        </Sub>

        <Toolbar>
          <Search>
            &#128269;
            <input
              value={q}
              placeholder="Search geo, currency, tier, band…"
              onChange={this.onFilter('q')}
            />
          </Search>
          <Select value={region} onChange={this.onFilter('region')}>
            <option value="">Region: All</option>
            {geos.map(g => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </Select>
          <Select value={currency} onChange={this.onFilter('currency')}>
            <option value="">Currency: All</option>
            {currencies.map(c => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
          <Select value={tier} onChange={this.onFilter('tier')}>
            <option value="">Tier: All</option>
            {tiers.map(t => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </Select>
        </Toolbar>

        <Formula>
          Per-class price = <code>amount &divide; no_of_classes</code>, derived live.
          Hover a price <b>value</b> for the underlying plans (subscription: weekly then
          monthly; admissions: per grade). <b className="conflict">Orange</b> = the plans
          behind that number disagree on per-class price.
        </Formula>

        <Card>
          <Scroll>
            <Table>
              <thead>
                <tr>
                  <Th left tight>
                    Geo &middot; grade band
                  </Th>
                  <Th left tight>
                    Cur
                  </Th>
                  <Th left tight>
                    Tier
                  </Th>
                  <Th dur>1-mo</Th>
                  <Th dur>3-mo</Th>
                  <Th dur>6-mo</Th>
                  <Th dur>12-mo</Th>
                  <Th dur>18-mo</Th>
                  <Th dur>24-mo</Th>
                </tr>
              </thead>
              <tbody>{rowsOut}</tbody>
            </Table>
          </Scroll>
        </Card>

        {this.renderModal()}
      </Wrap>
    );
  }
}

export default connect(
  ({ pricingMatrix }) => ({ pricingMatrix }),
  dispatch => ({
    actions: bindActionCreators({ getPricingMatrix }, dispatch),
  }),
)(PricingStructure);
